import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { getQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
import { useSelector } from 'react-redux';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { useAppSelector } from '../../reducers';
import { productsAndPacksForQueryParamsSelector } from '../../selectors/ProductConfigsSelectors';
import { productVersionFromProductInventorySelector, productsFromProductInventoryQuantitySelector } from '../../selectors/ProductInventorySelectors';
import { currentSkuIdForProductSelector, productIdForQuantityPackAndProductApiNameSelector, productVersionSelector } from '../../selectors/ProductsSelectors';
import { productDataSelector } from '../../selectors/SelectorUtils';
import { calculateProductQuantity, calculateQuantityPackQuantity, normalizeQueryParams, parseProductEntry, parseQuantityPackEntry, validateQuantity } from '../../v2/utils/queryParamProductUtils';
import { useQueryParamsEffect } from '../urls/appQueryParamUtilsLegacy';

// If a customer owns a legacy product, without this check it's possible for
// planBuilder to add both the legacy sku and the current sku to
// productConfigurations, so the product shows up twice (e.g. a bundle sku)
export const currentOrOwnedSkuIdSelector = (state, {
  productAttributeFilters
}) => {
  const ownedProductVersion = productVersionFromProductInventorySelector(state, {
    productAttributeFilters
  });
  const ownedSkuId = ownedProductVersion && ownedProductVersion.skuId;
  const currentSkuId = currentSkuIdForProductSelector(state, productAttributeFilters);
  return ownedSkuId || currentSkuId;
};

/**
 * Processes a single product configuration from query parameters
 */
const processProductConfig = (state, entry, apiNameToProductId) => {
  const {
    productApiName,
    quantityString,
    isIncremental,
    hasValidFormat
  } = parseProductEntry(entry);
  if (!hasValidFormat || !apiNameToProductId || !(productApiName in apiNameToProductId)) {
    return null;
  }
  const baseQuantity = validateQuantity(quantityString);
  const currentSkuId = currentOrOwnedSkuIdSelector(state, {
    productAttributeFilters: {
      apiName: productApiName
    }
  });

  // Add the incremental quantity to what the customer already owns
  const ownedQuantity = currentSkuId ? productsFromProductInventoryQuantitySelector(state, {
    skuId: currentSkuId
  }) : 0;
  const finalQuantity = calculateProductQuantity(baseQuantity, isIncremental, ownedQuantity);
  const skuId = currentOrOwnedSkuIdSelector(state, {
    productAttributeFilters: {
      apiName: productApiName
    }
  });
  return createProductConfig({
    skuId,
    quantity: finalQuantity
  });
};

/**
 * Processes a single quantity pack configuration from query parameters
 */
const processQuantityPackConfig = (state, entry) => {
  const {
    quantityPackApiName,
    parentProductApiName,
    quantityString,
    isIncremental,
    hasValidFormat
  } = parseQuantityPackEntry(entry);
  if (!hasValidFormat) {
    return null;
  }
  const baseQuantity = validateQuantity(quantityString);
  const productId = productIdForQuantityPackAndProductApiNameSelector(state, {
    quantityPackApiName: quantityPackApiName,
    productApiName: parentProductApiName
  });
  if (baseQuantity < 0 || !productId || !quantityPackApiName) {
    return null;
  }
  const parentSkuId = currentOrOwnedSkuIdSelector(state, {
    productAttributeFilters: {
      productId
    }
  });
  if (!parentSkuId) {
    return null;
  }
  const {
    quantityPackDetails
  } = productVersionSelector(state, {
    skuId: parentSkuId
  });
  const quantityPack = quantityPackDetails.find(({
    quantityPackApiName: searchQuantityPackApiName
  }) => quantityPackApiName === searchQuantityPackApiName);
  if (!quantityPack) {
    return null;
  }
  const {
    skuId,
    includedWithQuantityPack
  } = quantityPack;
  const ownedQuantity = productsFromProductInventoryQuantitySelector(state, {
    skuId
  }) || 0;
  const finalQuantity = calculateQuantityPackQuantity(baseQuantity, isIncremental, includedWithQuantityPack, ownedQuantity);
  return {
    skuId,
    quantity: finalQuantity
  };
};
export const queryParamProductConfigurationsSelector = (state, {
  queryParams
}) => {
  const productConfigurations = [];
  const {
    products,
    packs
  } = queryParams;
  const {
    apiNameToProductId
  } = productDataSelector(state);
  const {
    stringProductConfigs,
    stringQuantityPacks
  } = normalizeQueryParams(products, packs);

  // Process product configurations
  stringProductConfigs.forEach(entry => {
    const config = processProductConfig(state, entry, apiNameToProductId);
    if (config) {
      productConfigurations.push(config);
    }
  });

  // Process quantity pack configurations
  stringQuantityPacks.forEach(entry => {
    const config = processQuantityPackConfig(state, entry);
    if (config) {
      productConfigurations.push(config);
    }
  });
  return productConfigurations;
};
export const useQueryParamProductConfigsOrDefault = ({
  defaultConfigurations
}) => {
  const queryParams = getQueryParams();
  const isAppReady = useGlobalLoading();
  const queryParamProductConfigurations = useAppSelector(state => isAppReady ? queryParamProductConfigurationsSelector(state, {
    queryParams
  }) : []);
  if (queryParamProductConfigurations.length) {
    return queryParamProductConfigurations;
  }
  return defaultConfigurations;
};
export const useProductConfigsQueryParamsParamsEffect = productConfigurations => {
  const {
    products,
    packs
  } = useSelector(state => productsAndPacksForQueryParamsSelector(state, {
    productConfigurations
  }));
  useQueryParamsEffect({
    products,
    packs
  });
};